import MuleBadge from '../../components/MuleBadge';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import FilterBar from '../../components/FilterBar';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import ordersService from '../../services/orders.service';
import api from '../../services/api';
import { config } from '../../config/config';

interface OverrideApprovalRow {
  id: number;
  created_at: string | null;
  approved_by: string;
  pronumber: number;
  legnum: number;
  // legrecno is fetched but not displayed — kept for completeness / debugging.
  legrecno: number | null;
  driver: string;
  driver_name: string;
  isMule?: boolean;
  reason: string;
  driver_not_qualified_text: string;
  legtype: string;
  legaction: string;
  origin_name: string;
  origin_city_state: string;
  destination_name: string;
  destination_city_state: string;
  // Snapshot of order-level qualification inputs at time of approval.
  lbs: number | null;
  hazmat: string;
  tank: string;
  daycab: string;
  otr: string;
  size: string;
  equiptype: string;
  chassistype: string;
}

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatLocation = (name: string, cityState: string): string => {
  const parts = [name?.trim(), cityState?.trim()].filter(Boolean);
  return parts.length ? parts.join(' — ') : '-';
};

function DispatchOverrideApprovalsContent() {
  // Any orders-scope registry field can be added as a column here; the backend
  // attaches it to the rows this report already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('dispatch-override-approvals');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;
  const [data, setData] = useState<OverrideApprovalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Independent start/end date pickers (mirrors DriverEndpoints' free-at
  // split). Defaults to the last 30 days; either can be cleared but both
  // must be set for the server-side date filter to fire.
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => new Date());

  // Region selection is shared across all Researching / Reports pages via
  // the GlobalFilters context — switching pages preserves the user's pick.
  // Backend filters driver_disp.region (the driver dispatcher's region).
  const {
    selectedRegions,
    setSelectedRegions,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);

  // Order details drawer state — clicking the PRO # link opens the in-app
  // ProDetailSlideOver, which fetches/refreshes the order itself.
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const regionsRes = await ordersService.getRegions();
        setRegionOptions(
          regionsRes.data.regions.map((region: string) => ({
            value: region,
            label: region,
          })),
        );
      } catch (e) {
        console.error('Failed to fetch regions:', e);
      }
    };
    fetchRegions();
  }, []);

  const fetchData = useCallback(async (showLoading = false) => {
    if (!isMounted.current) return;
    if (!startDate || !endDate) return;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setError(null);
      setData([]);
      if (showLoading) setLoading(true);

      const params: Record<string, string> = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
      };
      if (selectedRegions.length > 0) {
        params.region = selectedRegions.join(',');
      }

      // The saved selection is part of the request; going early just fetches twice.
      if (!fieldsReady) return;
      if (additionalFieldsKey) params.additional_fields = additionalFieldsKey;

      const response = await api.get('/reporting/dispatch-override-approvals', {
        params,
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      if (isMounted.current && !controller.signal.aborted) setLoading(false);
    }
  }, [startDate, endDate, selectedRegions, fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    if (globalFiltersLoading) return;
    fetchData(true);
  }, [fetchData, globalFiltersLoading]);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);

  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  const baseColumns = useMemo<ColumnDef<OverrideApprovalRow>[]>(() => [
    {
      accessorKey: 'created_at',
      header: 'Approved At',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.created_at),
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'approved_by',
      header: 'Approved By',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'pronumber',
      header: 'PRO #',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const pronumber = row.original.pronumber;
        // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
        // trackViewedColumnId="pronumber" wrapper.
        return (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openOrderDrawer(Number(pronumber));
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pronumber}
            </button>
            <a
              href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              title="Open in CMSV1"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        );
      },
    },
    {
      accessorKey: 'legnum',
      header: 'Leg #',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<number>() ?? '-',
    },
    {
      accessorKey: 'legtype',
      header: 'Leg Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'legaction',
      header: 'Leg Action',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'origin',
      accessorFn: (row) => formatLocation(row.origin_name, row.origin_city_state),
      header: 'Origin',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'destination',
      accessorFn: (row) => formatLocation(row.destination_name, row.destination_city_state),
      header: 'Destination',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'driver_name',
      header: 'Driver',
      meta: { exportValue: (row: { original: OverrideApprovalRow }) => `${row.original.driver_name || ''} ${row.original.driver || ''}${row.original.isMule ? ' (Mule)' : ''}` },
      filterFn: 'arrIncludes',
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.driver_name || '-'}<MuleBadge isMule={row.original.isMule} /></div>
          {row.original.driver && (
            <div className="text-xs text-muted">{row.original.driver}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'driver_not_qualified_text',
      header: 'Qualification Failure',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'reason',
      header: 'Reason',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    // ---- Load snapshot (order-level qualification inputs at approval time) ----
    {
      accessorKey: 'lbs',
      header: 'Lbs',
      filterFn: 'arrIncludes',
      cell: (info) => {
        const v = info.getValue<number | null>();
        return v != null ? v.toLocaleString() : '-';
      },
      sortingFn: 'basic',
    },
    {
      accessorKey: 'hazmat',
      header: 'Hazmat',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'tank',
      header: 'Tank',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'daycab',
      header: 'Daycab',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'otr',
      header: 'OTR',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'size',
      header: 'Size',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'equiptype',
      header: 'Equip Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'chassistype',
      header: 'Chassis Type',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
  ], [openOrderDrawer]);

  // Rows are report rows, not orders — several can share a pro — so an added order
  // column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<OverrideApprovalRow>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: OverrideApprovalRow) => String(row.id), []);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <FilterBar label="">
          <div className="flex items-center gap-1">
            <span>Approved between</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={startDate}
              onChange={(val) => setStartDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="Start"
              autoApply
            />
            <span>and</span>
            <DateTimePicker
              mode="date"
              selectionType="single"
              value={endDate}
              onChange={(val) => setEndDate((val as Date | null) ?? null)}
              inputVariant="compact"
              labelType="none"
              placeholder="End"
              autoApply
            />
          </div>
          <FilterBar.Divider />
          <MultiSelect
            options={regionOptions}
            value={selectedRegions}
            onChange={setSelectedRegions}
            placeholder="All Regions"
            showClearOption={true}
            clearOptionLabel="All Regions"
            size="sm"
          />
        </FilterBar>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-red-500">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <FilterBar label="">
        <div className="flex items-center gap-1">
          <span>Approved between</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={startDate}
            onChange={(val) => setStartDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="Start"
            autoApply
          />
          <span>and</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={endDate}
            onChange={(val) => setEndDate((val as Date | null) ?? null)}
            inputVariant="compact"
            labelType="none"
            placeholder="End"
            autoApply
          />
        </div>
        <FilterBar.Divider />
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          size="sm"
        />
      </FilterBar>

      <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
        This table lists every driver qualification override approval recorded from the pro details save flow.
      </div>

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId="pronumber"
          loading={loading}
          defaultSorting={[{ id: 'created_at', desc: true }]}
          settingsKey="dispatch-override-approvals"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="Dispatch Override Approvals"
          fillContainer={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function DispatchOverrideApprovals() {
  return (
    <ViewedProsProvider>
      <DispatchOverrideApprovalsContent />
    </ViewedProsProvider>
  );
}

export default DispatchOverrideApprovals;
